import React, { useEffect, useState, ChangeEvent, useContext } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { useLocation, useHistory } from "react-router-dom";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import CredentialsForm from "@/components/Form/Form";
import MantraButton from "@/components/mantra-button/MantraButton";
import { FormError } from "@/models/continente-credentials/common/FormError";
import { useTranslation } from "react-i18next";
import { DigitalAssetContext } from "../../../contexts/continente-credentials/DigitalAssetContext";
import { CardAssociation } from "@/models/continente-credentials/mobile/CardAssociation";
import { cardValidationService } from "@/services/continente-credentials/mobile/CardValidationService";
import loyaltyCard from "@/assets/images/cartoes.png";
import { PasswordRecoverValidateIdentifierResponse } from "@/models/continente-credentials/mobile/PasswordRecoverValidateIdentifierResponse";
import ErrorMessage from "@/components/error-message/error-message";
import Input from "@/components/ui/Input";
import { Button } from "@/components/ui/button";
import ButtonContainer from "@/components/button-container";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import Divider from "@/components/divider";
import FreshStartButton from "@/components/fresh-start-button/fresh-start-button";
import { FreshStartOrigin } from "@/config/FreshStartOriginConfig";
import { FreshStartRequest, FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";
import { genericService } from "@/services/GenericService";

type Form = CardAssociation & FormError;

export default function LoyaltyCardValidation() {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const location = useLocation<PasswordRecoverValidateIdentifierResponse>();
  const history = useHistory();
  const digitalAssetContext = useContext(DigitalAssetContext);
  const [labelCardVisible, setLabelCardVisible] = useState<boolean>(false);
  const [cardValue, setCardValue] = useState<string>();
  const identifier =
    location.state != null ? location.state.properties.identifier : "";
  const phoneFreshStartEligible = location.state?.properties?.phoneFreshStartEligible === true;

  const nextStep = location.state?.nextStep || "";
  const { handleResponse } = useApiResponseHandler();

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleWhereToFindCardNumberClick = () => {
    window.open("/continente-credentials/faq", "_self");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key !== "Enter" &&
      (!e.key.match(/[0-9\s]/) ||
        (e.target as HTMLInputElement).value.length > 12)
    ) {
      e.preventDefault();
    }
  };

  const handleCardChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();

    let cardNumber = value.replace(/\s/g, "");

    setValue("cardNumber", cardNumber);
    setLabelCardVisible(cardNumber.length > 0 ? true : false);
    setCardValue(cardNumber);
  };

  const onSubmit: SubmitHandler<CardAssociation> = async (data) => {
    setLoading(true);

    const { data: response, error } = await tryCatch(cardValidationService.cardValidation({
      identifier: identifier,
      cardNumber: data.cardNumber,
    }, nextStep));

    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  const freshStart = async () => {
    clearErrors();
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse, FreshStartRequest>(
      "api/phonenumber/login/fresh-start",
      {
        origin: FreshStartOrigin.PASSWORD_RECOVER_LOYALTY_CARD
      }
    ));

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  useEffect(() => {
    const CardValidator = (register: UseFormRegister<Form>) =>
      register("cardNumber", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]{13}$/,
          message: t("card_association.invalid_loyaltycard"),
        },
      });
    CardValidator(register);
  }, [register, t]);

  return (
    <CredentialsForm
      id="CardAssociation"
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t("card_validation.header_title")}
        subtitle={t("card_validation.header_message")}
        showLoading={loading}
      />
      <img src={loyaltyCard} className="w-[240px] p-0 my-4" alt="" />
      <Input
        labelText={t("card_validation.card_number_input")}
        isFilled={labelCardVisible}
        isError={!!(errors.cardNumber || errors.blocked || errors.submitError)}
        errorMsg={
          errors.cardNumber?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        onChange={handleCardChange}
        onKeyPress={handleKeyPress}
        value={cardValue}
        category="cardNumber"
      />
      <ButtonContainer>
        <Button variant={"link"}
          size={"link"} className="font-bold" onClick={handleWhereToFindCardNumberClick}>
          {t("card_validation.where_to_find_message")}
        </Button>
        <Button type="submit" >
          {t("card_validation.button_next_message")}
        </Button>
      </ButtonContainer>
      {phoneFreshStartEligible && (
        <div className="w-full flex flex-col gap-6">
          <Divider title={t("common.or")} />
          <FreshStartButton onClick={freshStart} />
        </div>
      )}
    </CredentialsForm>
  );
}
